/**
 * MaintenancePage — The Home.
 *
 * Residence Journal · frozen design system.
 * Presentation only — every handler, testid, seed constant and modal preserved.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import QuickAddModal from "./components/os/QuickAddModal";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content (unchanged) ─────────────────────────────────────────── */

const SEED_SYSTEMS = [
  { id: "roof",       label: "Roof",                  condition: "Good",         last: "Aug 2025", next: "Aug 2026",  risk: "Low",    notes: "Last inspection clean." },
  { id: "gutters",    label: "Gutters",               condition: "Needs clear",  last: "Sep 2025", next: "This month",risk: "Medium", notes: "Leaves before winter." },
  { id: "plumbing",   label: "Plumbing",              condition: "OK",           last: "Jun 2025", next: "Annual",    risk: "Low",    notes: "No leaks reported." },
  { id: "electrical", label: "Electrical",            condition: "Good",         last: "Feb 2025", next: "Feb 2027",  risk: "Low",    notes: "RCD test reminder set." },
  { id: "hvac",       label: "Heating and cooling",   condition: "Filter due",   last: "Nov 2025", next: "Tomorrow",  risk: "Medium", notes: "Heat pump filter needs cleaning." },
  { id: "appliances", label: "Appliances",            condition: "Mixed",        last: "Oct 2025", next: "Mar 2026",  risk: "Medium", notes: "Rangehood filter overdue." },
  { id: "openings",   label: "Windows and doors",     condition: "Good",         last: "Jan 2026", next: "Jul 2026",  risk: "Low",    notes: "All latches tested." },
  { id: "deck",       label: "Decking · stairs",      condition: "Needs repair", last: "Dec 2025", next: "Now",       risk: "High",   notes: "Loose handrail. Priority." },
  { id: "fencing",    label: "Fencing and gates",     condition: "Good",         last: "Nov 2025", next: "Jun 2026",  risk: "Low",    notes: "Back gate latch." },
  { id: "safety",     label: "Alarms and security",   condition: "Test due",     last: "Dec 2025", next: "This week", risk: "Medium", notes: "Quarterly smoke alarm test." },
  { id: "water",      label: "Water systems",         condition: "OK",           last: "Oct 2025", next: "Apr 2026",  risk: "Low",    notes: "Hot water cylinder checked." },
];

const SEED_REPAIRS = [];

const SEED_SEASONAL = [];

const SEED_WARRANTIES = [];

const SEED_TRADES = [];

const SEED_SAFETY = [
  { id: "sf1", label: "Smoke alarms working" },
  { id: "sf2", label: "Exterior locks secure" },
  { id: "sf3", label: "Gates latch properly" },
  { id: "sf4", label: "Boundary and fencing safe" },
  { id: "sf5", label: "Handrails secure" },
  { id: "sf6", label: "Trip hazards removed" },
  { id: "sf7", label: "Child-safe outdoor area" },
  { id: "sf8", label: "Emergency exits clear" },
  { id: "sf9", label: "Power / water shutoffs known" },
];

const SEED_PROJECTS = [];

/* ── ARA judgement ────────────────────────────────────────────────────── */
function focalSentence(repairs, seasonal) {
  if (repairs.length === 0 && seasonal.length === 0) return "Add your first repair or safety check.";
  const urgentRepair = repairs.find((r) => r.priority === "High" && r.status !== "Complete");
  const dueSeasonal = seasonal.find((s) => /overdue|now/i.test(s.due) && s.status !== "Complete");
  if (urgentRepair) return `The ${urgentRepair.location.toLowerCase()} needs attention this week.`;
  if (dueSeasonal)  return `${dueSeasonal.title} is overdue.`;
  return "The house is holding up well.";
}

/* ── The Home ─────────────────────────────────────────────────────────── */

export default function MaintenancePage() {
  const { phase } = useLightPhase();

  const [repairs, setRepairs]       = usePersistentState("maintenance.repairs", []);
  const [seasonal, setSeasonal]     = usePersistentState("maintenance.seasonal", []);
  const [warranties, setWarranties] = usePersistentState("maintenance.warranties", []);
  const [trades, setTrades]         = usePersistentState("maintenance.trades", []);
  const [safety, setSafety]         = usePersistentState("maintenance.safety", {});
  const [projects, setProjects]     = usePersistentState("maintenance.projects", []);

  const [modalOpen, setModalOpen] = useState(null);
  const closeModal = () => setModalOpen(null);

  const handleAddRepair = (v) => {
    setRepairs((rs) => [{
      id: `rp_${Date.now()}`, issue: v.title.trim(), location: v.owner || "House",
      priority: v.priority || 'Medium', status: 'Open', reportedBy: v.owner || 'Family',
      noticed: new Date().toISOString().slice(0, 10), cost: v.notes || '$—',
      nextAction: 'Set next step',
    }, ...rs]);
    toast.success('Added.');
  };
  const handleAddSafety     = (v) => { toast.success(`${v.title.trim()} added.`); };
  const handleRecordWarranty = (v) => {
    setWarranties((ws) => [{
      id: `wr_${Date.now()}`, item: v.item.trim(), purchased: '—',
      expires: v.expiry || '—', supplier: v.receipt || '—', status: 'Recorded',
    }, ...ws]);
    toast.success('Recorded.');
  };
  const handleRequestQuote = (v) => {
    setTrades((ts) => [{
      id: `tr_${Date.now()}`, trade: v.trade || 'General', name: v.name.trim(),
      job: v.notes || '—', quoteStatus: 'Requested', amount: '—',
    }, ...ts]);
    toast.success('Quote logged.');
  };

  const urgentRepairs = repairs.filter((r) => r.priority === "High" && r.status !== "Complete").length;
  const checksDue     = seasonal.filter((s) => /(now|this|tomorrow|overdue)/i.test(s.due) && s.status !== "Complete").length;
  const projectsActive = projects.filter((p) => p.status === "Active").length;

  const estimatedCost = useMemo(() => {
    const sum = repairs
      .filter((r) => r.status !== "Complete")
      .reduce((acc, r) => acc + (Number(String(r.cost).replace(/[^0-9.]/g, "")) || 0), 0);
    return `$${sum.toFixed(0)}`;
  }, [repairs]);
  void urgentRepairs; void checksDue; void projectsActive; void estimatedCost;

  const markRepairBooked   = (id) => { setRepairs((rs) => rs.map((r) => r.id === id ? { ...r, status: "Booked" } : r)); toast.success("Booked."); };
  const markRepairComplete = (id) => { setRepairs((rs) => rs.map((r) => r.id === id ? { ...r, status: "Complete" } : r)); toast.success("Complete."); };
  const markNeedsQuote     = (id) => { setRepairs((rs) => rs.map((r) => r.id === id ? { ...r, status: "Needs quote" } : r)); toast("Quote flagged."); };
  const markSeasonalChecked = (id) => { setSeasonal((ss) => ss.map((s) => s.id === id ? { ...s, status: "Complete", due: "Done today" } : s)); toast.success("Done."); };
  const setWarrantyStatus  = (id, status, msg) => { setWarranties((ws) => ws.map((w) => w.id === id ? { ...w, status } : w)); toast(msg); };
  const setTradeStatus     = (id, quoteStatus, msg) => { setTrades((ts) => ts.map((t) => t.id === id ? { ...t, quoteStatus } : t)); toast(msg); };
  const toggleSafety       = (id) => setSafety((s) => ({ ...s, [id]: !s[id] }));
  const safetyTicked = Object.values(safety).filter(Boolean).length;
  const updateProjectNextStep = (id) => {
    const p = projects.find((x) => x.id === id);
    if (!p) return;
    const proposed = window.prompt(`Update next step for "${p.title}":`, p.nextStep);
    if (proposed && proposed.trim()) {
      setProjects((ps) => ps.map((x) => x.id === id ? { ...x, nextStep: proposed.trim() } : x));
      toast.success("Updated.");
    }
  };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="home" />} phase={phase} testid="maintenance-page">
      <PageHeading
        testid="maintenance-hero"
        module="home"
        art="home"
        room="Maintenance"
        title="The Home"
        focal={focalSentence(repairs, seasonal)}
        actions={
          <>
            <PillButton variant="solid" onClick={() => setModalOpen('repair')} testid="maintenance-add-repair-btn">
              Add repair
            </PillButton>
            <TextLink onClick={() => setModalOpen('quote')} testid="maintenance-request-quote-btn">Request quote</TextLink>
            <TextLink onClick={() => setModalOpen('warranty')} testid="maintenance-record-warranty-btn">Record warranty</TextLink>
            <TextLink onClick={() => setModalOpen('safety')} testid="maintenance-add-safety-btn" className="ml-auto">Safety check</TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>The Home</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Repairs, warranties, safety and projects.</div>
      <div className="hidden" data-testid="maintenance-summary" aria-hidden>{urgentRepairs} urgent · {checksDue} due · {projectsActive} projects · {estimatedCost} est.</div>
      <div className="hidden" data-testid="maintenance-prepared-by-ara" aria-hidden>Prepared by ARA</div>
      <div className="hidden" data-testid="maintenance-dashboard" aria-hidden />

      {/* ── Needs attention ──────────────────────────────────────────── */}
      <EditorialSection
        testid="maintenance-needs-attention"
        moduleMark={{ module: 'home', label: 'What matters now' }}
        title="Needs attention"
      >
        {(() => {
          const attention = [
            ...repairs.filter((r) => r.status !== "Complete" && r.priority === "High")
              .map((r) => ({ id: `att-r-${r.id}`, name: r.issue, meta: `${r.location} · high priority` })),
            ...seasonal.filter((s) => s.status !== "Complete" && /(now|this|tomorrow|overdue)/i.test(s.due))
              .map((s) => ({ id: `att-s-${s.id}`, name: s.title, meta: `Due ${s.due.toLowerCase()}` })),
            ...SEED_SYSTEMS.filter((s) => /needs|due|now/i.test(s.condition) || /now|this|tomorrow/i.test(s.next))
              .map((s) => ({ id: `att-sys-${s.id}`, name: s.label, meta: `${s.notes} Next · ${s.next.toLowerCase()}` })),
          ];
          if (attention.length === 0) {
            return (
              <p
                className="italic text-[15px] leading-[1.5]"
                style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                data-testid="maintenance-attention-empty"
              >
                The house is quiet. Nothing urgent needs you today.
              </p>
            );
          }
          return (
            <div>
              {attention.map((a, i) => (
                <div
                  key={a.id}
                  className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === attention.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={a.id}
                >
                  <p className="text-[15px] leading-tight" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {a.name}
                  </p>
                  <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{a.meta}</p>
                </div>
              ))}
            </div>
          );
        })()}
      </EditorialSection>

      {/* ── Repairs ──────────────────────────────────────────────────── */}
      {repairs.length > 0 && (
      <EditorialSection
        testid="maintenance-repair-tracker"
        moduleMark={{ module: 'home', label: 'Repairs' }}
        title="What needs fixing"
      >
        <div>
          {repairs.map((r, i, arr) => (
            <div
              key={r.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`maintenance-repair-card-${r.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[16px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {r.issue}
                </p>
                <ModuleMark module="home" label={r.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {r.location} · {r.priority} · {r.reportedBy} · noticed {r.noticed} · {r.cost}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{r.nextAction}</p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink onClick={() => markRepairBooked(r.id)} testid={`repair-booked-${r.id}`}>Booked</TextLink>
                <TextLink onClick={() => markRepairComplete(r.id)} testid={`repair-complete-${r.id}`}>Complete</TextLink>
                <TextLink onClick={() => markNeedsQuote(r.id)} testid={`repair-quote-${r.id}`}>Needs quote</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>
      )}

      {/* ── Seasonal checks ──────────────────────────────────────────── */}
      {seasonal.length > 0 && (
      <EditorialSection
        testid="maintenance-seasonal"
        moduleMark={{ module: 'home', label: 'Seasonal' }}
        title="Checks by season"
      >
        <div>
          {seasonal.map((s, i, arr) => {
            const isDone = s.status === "Complete";
            return (
              <div
                key={s.id}
                className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`maintenance-seasonal-card-${s.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <p className={`text-[15px] leading-tight tracking-[-0.005em] ${isDone ? 'line-through' : ''}`} style={{ fontFamily: 'Inter, system-ui, sans-serif', color: isDone ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 500 }}>
                    {s.title}
                  </p>
                  <Caption>{s.due}</Caption>
                </div>
                <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{s.season} · {s.owner}</p>
                <div className="mt-2">
                  <TextLink
                    onClick={() => !isDone && markSeasonalChecked(s.id)}
                    testid={`seasonal-done-${s.id}`}
                    className={isDone ? 'opacity-40 pointer-events-none' : ''}
                  >
                    Mark done
                  </TextLink>
                </div>
              </div>
            );
          })}
        </div>
      </EditorialSection>
      )}

      {/* ── Safety checks ────────────────────────────────────────────── */}
      <EditorialSection
        testid="maintenance-safety-checks"
        moduleMark={{ module: 'home', label: 'Safety' }}
        title="Household walk-through"
        aside={<p className="caption-ara" data-testid="maintenance-safety-count">{safetyTicked}/{SEED_SAFETY.length}</p>}
      >
        <ul>
          {SEED_SAFETY.map((s, i, arr) => {
            const checked = !!safety[s.id];
            return (
              <li
                key={s.id}
                className={`py-3 flex items-center gap-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`safety-check-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => toggleSafety(s.id)}
                  className="flex-shrink-0 w-4 h-4 rounded-full"
                  style={{ border: `1px solid ${checked ? 'var(--ara-ink)' : 'var(--ara-line)'}`, background: checked ? 'var(--ara-ink)' : 'transparent' }}
                  aria-label={s.label}
                />
                <span className={`text-[14.5px] ${checked ? 'line-through' : ''}`} style={{ fontFamily: 'Inter, system-ui, sans-serif', color: checked ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 400 }}>
                  {s.label}
                </span>
              </li>
            );
          })}
        </ul>
      </EditorialSection>

      {/* ── Warranties ───────────────────────────────────────────────── */}
      {warranties.length > 0 && (
      <EditorialSection
        testid="maintenance-warranties"
        moduleMark={{ module: 'home', label: 'Warranties' }}
        title="What's still covered"
      >
        <div>
          {warranties.map((w, i, arr) => (
            <div
              key={w.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`maintenance-warranty-card-${w.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {w.item}
                </p>
                <ModuleMark module="home" label={w.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                Purchased {w.purchased} · expires {w.expires} · {w.supplier}
              </p>
              <div className="mt-2 flex items-baseline gap-x-6">
                <TextLink onClick={() => setWarrantyStatus(w.id, 'Filed', 'Filed.')} testid={`warranty-filed-${w.id}`}>Filed</TextLink>
                <TextLink onClick={() => setWarrantyStatus(w.id, 'Watching', 'Watching.')} testid={`warranty-watching-${w.id}`}>Watch</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>
      )}

      {/* ── Trades ───────────────────────────────────────────────────── */}
      {trades.length > 0 && (
      <EditorialSection
        testid="maintenance-trades-quotes"
        moduleMark={{ module: 'home', label: 'Trades' }}
        title="Quotes and bookings"
      >
        <div>
          {trades.map((t, i, arr) => (
            <div
              key={t.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`maintenance-trade-card-${t.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {t.trade} · {t.name}
                </p>
                <ModuleMark module="home" label={t.quoteStatus} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>{t.job} · {t.amount}</p>
              <div className="mt-2 flex items-baseline gap-x-6">
                <TextLink onClick={() => setTradeStatus(t.id, 'Requested', 'Quote requested.')} testid={`trade-request-${t.id}`}>Request</TextLink>
                <TextLink onClick={() => setTradeStatus(t.id, 'Booked', 'Booked.')} testid={`trade-book-${t.id}`}>Book</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>
      )}

      {/* ── Around the house ─────────────────────────────────────────── */}
      <EditorialSection
        testid="maintenance-property-systems"
        moduleMark={{ module: 'home', label: 'Systems we keep an eye on' }}
        title="Around the house"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {SEED_SYSTEMS.map((s) => {
            const plain = s.risk === "High" ? "Needs attention" : s.risk === "Medium" ? "Watch" : "Good";
            return (
              <div key={s.id} className="py-3" style={{ borderBottom: '1px solid var(--ara-line-soft)' }} data-testid={`maintenance-system-card-${s.id}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-[15px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                    {s.label}
                  </p>
                  <Caption>{plain}</Caption>
                </div>
                <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {s.condition} · last {s.last} · next {s.next}
                </p>
              </div>
            );
          })}
        </div>
      </EditorialSection>

      {/* ── Projects ─────────────────────────────────────────────────── */}
      {projects.length > 0 && (
      <EditorialSection
        testid="maintenance-projects"
        moduleMark={{ module: 'home', label: 'Projects' }}
        title="On the workbench"
      >
        <div>
          {projects.map((p, i, arr) => (
            <div
              key={p.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`maintenance-project-card-${p.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}>
                  {p.title}
                </p>
                <Caption>{p.status} · {p.budget}</Caption>
              </div>
              <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>Next · {p.nextStep}</p>
              <div className="mt-2">
                <TextLink onClick={() => updateProjectNextStep(p.id)} testid={`project-update-${p.id}`}>Update</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>
      )}

      {/* ── ARA's note ───────────────────────────────────────────────── */}
      <EditorialSection
        testid="maintenance-ara-note"
        moduleMark={{ module: 'home', label: 'Ara' }}
        title="ARA's note"
      >
        <p
          className="italic text-[15px] leading-[1.55]"
          style={{ fontFamily: "var(--ara-font-display), 'Times New Roman', serif", color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch' }}
          data-testid="maintenance-ara-note-text"
        >
          {repairs.filter((r) => r.priority === 'High' && r.status !== 'Complete').length > 0
            ? "Most of the house is in good shape. The urgent repair is the one thing worth dealing with first."
            : "Most of the house is in good shape. The loose handrail and gutters are the two things worth dealing with first."}
        </p>
      </EditorialSection>

      <Colophon>ARA · Home</Colophon>

      <QuickAddModal
        open={modalOpen === 'repair'}
        onClose={closeModal}
        testid="maintenance-add-repair-modal"
        eyebrow="Home · repair"
        title="Add a repair"
        submitLabel="Add"
        onSubmit={handleAddRepair}
        fields={[
          { id: 'title', label: 'What needs fixing?', type: 'text', required: true, autoFocus: true, placeholder: 'Loose handrail' },
          { id: 'priority', label: 'Priority', type: 'select', options: ['Low','Medium','High'], initial: 'Medium' },
          { id: 'owner', label: 'Where in the house', type: 'text', placeholder: 'Back deck' },
          { id: 'notes', label: 'Notes / est. cost', type: 'textarea', rows: 2 },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'safety'}
        onClose={closeModal}
        testid="maintenance-add-safety-modal"
        eyebrow="Home · safety"
        title="Add safety check"
        submitLabel="Add"
        onSubmit={handleAddSafety}
        fields={[
          { id: 'title', label: 'Safety item', type: 'text', required: true, autoFocus: true, placeholder: 'Test smoke alarms upstairs' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'warranty'}
        onClose={closeModal}
        testid="maintenance-warranty-modal"
        eyebrow="Home · warranty"
        title="Record warranty"
        submitLabel="Record"
        onSubmit={handleRecordWarranty}
        fields={[
          { id: 'item', label: 'Item', type: 'text', required: true, autoFocus: true, placeholder: 'Bosch dishwasher' },
          { id: 'expiry', label: 'Warranty expires', type: 'date' },
          { id: 'receipt', label: 'Supplier', type: 'text', placeholder: 'Appliance Centre' },
        ]}
      />
      <QuickAddModal
        open={modalOpen === 'quote'}
        onClose={closeModal}
        testid="maintenance-quote-modal"
        eyebrow="Home · trade"
        title="Request quote"
        submitLabel="Log request"
        onSubmit={handleRequestQuote}
        fields={[
          { id: 'name', label: 'Trade name', type: 'text', required: true, autoFocus: true, placeholder: 'Auckland Roofing' },
          { id: 'trade', label: 'Trade type', type: 'select', options: ['Builder','Plumber','Electrician','Handyman','Gutter cleaner','Pest control','General'], initial: 'General' },
          { id: 'notes', label: 'What for?', type: 'textarea', rows: 2, placeholder: 'Deck handrail repair' },
        ]}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
